<script setup lang="ts">
import type { MapLocation } from "~/composables/useGoogleMap";


interface MapValue {
    lat: number
    lng: number
    address?: string | null
}

const props = defineProps({
    initialValue: {
        type: Object as PropType<MapValue | null>,
        default: null
    },
    center: {
        type: Object as PropType<MapLocation>,
        default: () => ({
            lat: 25.081233,
            lng: 55.228174,
        })
    },
    zoom: {
        type: Number,
        default: 6
    },
    minHeight: {
        type: String,
        default: '400px'
    },
    maxHeight: {
        type: String,
        default: null
    },
    disabled: {
        type: Boolean,
        default: false
    },
    showMarker: {
        type: Boolean,
        default: true
    },
    clickable: {
        type: Boolean,
        default: true
    },
    mapId: {
        type: String,
        default: 'dae7e15278b8ac1650232714'
    },
    aspectRatio: {
        type: String,
        default: '640/400'
    }
})

const emit = defineEmits<{
    'update:modelValue': [value: MapValue | null]
    'ready': [event: any]
    'click': [location: MapLocation, address: string | null]
    'markerClick': [location: MapLocation]
    'placeSelected': [placeId: string, location: MapLocation, address: string | null]
}>()

const {
    googleMapsRef,
    mapOptions,
    selectedLocation,
    markerContent,
    getAddressFromCoordinates,
    getPlaceDetails,
    getCurrentLocation,
    initializeMap,
} = useGoogleMap()

const isLoadingLocation = ref(false)
const mapInstance = ref<google.maps.Map | null>(null)

mapOptions.value.center = props.center
mapOptions.value.zoom = props.zoom

const parseInitialValue = (value: string | MapValue | null): MapValue | null => {
    if (!value) return null
    
    if (typeof value === 'string') {
        try {
            return JSON.parse(value)
        } catch {
            return null
        }
    }
    
    return value
}

async function handleMapReady({ map }: { map: Ref<google.maps.Map> }) {
    emit('ready', map)
    mapInstance.value = map.value
    
    if (props.disabled || !map.value) return
    
    await nextTick()
    
    // Добавляем слушатель клика только если карта кликабельна
    if (props.clickable) {
        try {
            const clickHandler = async (clickEvent: google.maps.MapMouseEvent) => {
                if (clickEvent.latLng) {
                    const lat = clickEvent.latLng.lat()
                    const lng = clickEvent.latLng.lng()
                    
                    const location: MapLocation = { lat, lng }
                    const address = await getAddressFromCoordinates(lat, lng)
                    
                    selectedLocation.value = location
                    
                    const value: MapValue = { lat, lng, address }
                    emit('update:modelValue', value)
                    emit('click', location, address)
                }
            }
            
            map.value.addListener('click', clickHandler)
        } catch (error) {
            console.error('Error adding map listener:', error)
        }
    }

    const initialData = parseInitialValue(props.initialValue)
    if (initialData?.lat && initialData?.lng) {
        selectedLocation.value = {
            lat: initialData.lat,
            lng: initialData.lng
        }
        mapOptions.value.center = selectedLocation.value
    } else {
        try {
            const currentLocation = await getCurrentLocation()
            mapOptions.value.center = currentLocation
        } catch (error: any) {
            // При ошибке геолокации используем дефолтные координаты из props
            mapOptions.value.center = props.center
            // Логируем только если это не таймаут
            if (error?.code !== 3) {
                console.warn('Error getting location, using default center:', error?.message || error)
            }
        }
    }
}

watch(() => props.initialValue, (newInitialValue) => {
    if (newInitialValue) {
        const initialData = parseInitialValue(newInitialValue)
        if (initialData?.lat && initialData?.lng) {
            selectedLocation.value = {
                lat: initialData.lat,
                lng: initialData.lng
            }
            mapOptions.value.center = selectedLocation.value
        }
    }
}, { deep: true, immediate: true })

watch(() => props.center, (newCenter) => {
    if (newCenter) {
        mapOptions.value.center = newCenter
    }
}, { deep: true })

watch(() => props.zoom, (newZoom) => {
    if (newZoom) {
        mapOptions.value.zoom = newZoom
    }
})

const setLocation = (location: MapLocation, zoom = 6) => {
    selectedLocation.value = location
    mapOptions.value.center = location
    mapOptions.value.zoom = zoom
}

const clearLocation = () => {
    selectedLocation.value = null
    mapOptions.value.zoom = 6
}

const selectPlace = async (placeId: string) => {
    try {
        const place = await getPlaceDetails(placeId)
        if (place?.geometry?.location) {
            const lat = place.geometry.location.lat()
            const lng = place.geometry.location.lng()
            const address = place.name || null
            
            const location: MapLocation = { lat, lng }
            setLocation(location)
            
            const value: MapValue = { lat, lng, address }
            emit('update:modelValue', value)
            emit('click', location, address)
            emit('placeSelected', placeId, location, address)
            
            return { location, address }
        }
    } catch (error) {
        console.error('Error selecting place:', error)
        return null
    }
}

const goToCurrentLocation = async () => {
    if (props.disabled) return
    
    isLoadingLocation.value = true
    try {
        const location = await getCurrentLocation()
        const address = await getAddressFromCoordinates(location.lat, location.lng)
        
        setLocation(location)
        
        const value: MapValue = { ...location, address }
        emit('update:modelValue', value)
        emit('click', location, address)
        
        return { location, address }
    } catch (error: any) {
        // Логируем только если это не таймаут (таймаут - это нормальная ситуация)
        if (error?.code !== 3) {
            console.error('Error getting current location:', error?.message || error)
        }
        return null
    } finally {
        isLoadingLocation.value = false
    }
}

const getMap = () => {
    return googleMapsRef.value?.map
}

const zoomIn = () => {
    if (mapInstance.value) {
        const currentZoom = mapInstance.value.getZoom() || mapOptions.value.zoom
        const newZoom = Math.min(currentZoom + 1, 21)
        mapInstance.value.setZoom(newZoom)
        //mapOptions.value.zoom = newZoom
    } else {
        mapOptions.value.zoom = Math.min(mapOptions.value.zoom + 1, 21)
    }
}

const zoomOut = () => {
    if (mapInstance.value) {
        const currentZoom = mapInstance.value.getZoom() || mapOptions.value.zoom
        const newZoom = Math.max(currentZoom - 1, 1)
        mapInstance.value.setZoom(newZoom)
        //mapOptions.value.zoom = newZoom
    } else {
        mapOptions.value.zoom = Math.max(mapOptions.value.zoom - 1, 1)
    }
}

const fitBounds = (bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number } | null, padding = 50) => {
    const map = mapInstance.value || getMap()
    if (!map || !bounds) return
    
    const boundsObj = new google.maps.LatLngBounds(
        new google.maps.LatLng(bounds.minLat, bounds.minLng),
        new google.maps.LatLng(bounds.maxLat, bounds.maxLng)
    )
    
    map.fitBounds(boundsObj, padding)
}

defineExpose({
    setLocation,
    clearLocation,
    selectPlace,
    goToCurrentLocation,
    getMap,
    selectedLocation,
    mapOptions,
    isLoadingLocation,
    zoomIn,
    zoomOut,
    fitBounds
})
</script>

<template>
    <div class="relative w-full">
        <client-only>
            <div 
                class="relative w-full rounded-lg overflow-hidden"
                :style="{ 
                    minHeight, 
                    maxHeight, 
                    aspectRatio: aspectRatio 
                }"
            >
                <ScriptGoogleMaps
                    ref="googleMapsRef"
                    :center="mapOptions.center"
                    :zoom="mapOptions.zoom"
                    :map-options="{ 
                        mapId: mapId,
                        disableDefaultUI: true,
                        language: 'en_US',
                        clickableIcons: false,
                        gestureHandling: disabled ? 'none' : 'greedy'
                    }"
                    :class="[
                        '!absolute inset-0 w-full h-full rounded-lg',
                        disabled ? 'pointer-events-none opacity-75' : ''
                    ]"
                    :style="{ aspectRatio: 'unset', width: '100%', height: '100%' }"
                    :center-marker="false"
                    @ready="handleMapReady"
                    above-the-fold
                >
                <ScriptGoogleMapsAdvancedMarkerElement
                    v-if="showMarker && selectedLocation && markerContent"
                    :options="{ 
                        position: selectedLocation,
                        content: markerContent
                    }"
                />
                
                    <slot name="markers" :selected-location="selectedLocation" />
                </ScriptGoogleMaps>
            </div>
            
            <slot 
                :selected-location="selectedLocation" 
                :is-loading-location="isLoadingLocation"
                :set-location="setLocation"
                :clear-location="clearLocation"
                :select-place="selectPlace"
                :go-to-current-location="goToCurrentLocation"
                :map-options="mapOptions"
                :map="getMap()"
                :zoom-in="zoomIn"
                :zoom-out="zoomOut"
            />
        </client-only>
    </div>
</template>

